import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Button,
  PopupWindow,
  Table,
  MessagePopupCore,
} from '@utahdts/dts-react-common';
import Icon from '../misc/Icon';
import appStore from '../app/mobx/appStore';
import columns from './columns/UserColumns';
import webservice from '../app/webservice/webservice';
import User from './modals/User';
import AddOrgAdmin from './modals/AddOrgAdmin';
import security from '../app/roles/security';
import roles from '../app/roles/roles';
import Pages from '../app/pages/Pages';
import { usePrevious } from '../misc/hooks/usePrevious';
import { sortByLastUpdatedDesc } from '../misc/Shared';

const Users = () => {
  const [isBusy, setBusy] = useState(true);
  const [users, setUsers] = useState([]);
  const [showUser, displayUser] = useState(false);
  const [showDelete, displayDelete] = useState(false);
  const [user, setUser] = useState({});
  const [isNew, setIsNew] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [showAddOrgAdmin, displayAddOrgAdmin] = useState(false);
  const prevOrg = usePrevious(appStore.currentOrg);

  const displayError = () => {
    setBusy(false);
    MessagePopupCore.addMessage({
      title: 'Something went wrong',
      message: 'An error occurred. Please try again later.',
    });
  };

  const editUser = (isNew, user, showUser) => {
    setBusy(true);
    setIsNew(isNew);
    setUser(user);
    if (security.hasAnyRole([roles.dev, roles.admin]) && showUser) {
      webservice.company.all().then((data) => {
        let orgs = data.orgs.map((org) => {
          let option = { ...org };
          // The role's `org` relationship is the org FEIN (business key, see
          // .claude/rules/03-relationship-fields.md / saveUserMutation), so the
          // company dropdown options MUST be keyed by the fein to match it and
          // render the company name. Never the document id.
          option.value = org.fein;
          option.label = org.name || org.fein;
          return option;
        });
        setOrgs(orgs);
        setBusy(false);
        displayUser(showUser);
      });
    } else if (appStore.currentOrg && showUser) {
      const record = appStore.currentOrgRecord;
      setOrgs(
        record
          ? [{ ...record, value: record.fein, label: record.name || record.fein }]
          : [],
      );
      setBusy(false);
      displayUser(showUser);
    } else {
      setBusy(false);
      displayUser(showUser);
    }
  };

  const deleteUser = (user) => {
    setUser(user);
    displayDelete(true);
  };

  // A candidate is already an Org Administrator of the CURRENT org when one of
  // their roles is the org-admin role bound to this org. The role's `org`
  // relationship is the org FEIN (business key); tolerate a legacy document id
  // and the enriched `fein` field while matching.
  const isOrgAdminForCurrentOrg = (candidate) => {
    const company = appStore.currentOrgRecord;
    const variants = [
      appStore.currentOrg,
      company && company.fein,
      company && company.id,
    ].filter(Boolean);
    return (candidate.roles || []).some(
      (r) =>
        r &&
        r.role === roles.orgAdmin &&
        (variants.includes(r.org) || variants.includes(r.fein)),
    );
  };

  // Open the "Add Org Administrator" form for the currently selected company.
  // The form collects the required pharmacy-user fields; no existing users are
  // listed. On submit saveOrgAdmin attaches the role to a matching user or
  // creates a new one.
  const openAddOrgAdmin = () => displayAddOrgAdmin(true);

  const closeAddOrgAdmin = () => displayAddOrgAdmin(false);

  // Add an Org Administrator for the current org from the submitted form. The
  // email is the pharmacy-user business key: if a user with that email already
  // exists, the Org Administrator role is attached to that existing document;
  // otherwise a new pharmacy-user document is created carrying the role. The
  // role's `org` is the org FEIN business key (see
  // .claude/rules/03-relationship-fields.md), never a document id.
  const saveOrgAdmin = (formUser) => {
    const company = appStore.currentOrgRecord;
    const fein = (company && company.fein) || appStore.currentOrg;
    const newRole = {
      org: fein,
      fein,
      role: roles.orgAdmin,
      primaryContact: false,
      alternativeContact: false,
    };
    const email = (formUser.email || '').trim();
    return webservice.user
      .perEmail(email)
      .then((data) => {
        const existing = data ? data.userByEmail : undefined;
        let updated;
        if (existing) {
          // Existing user: attach the Org Administrator role for this org,
          // avoiding a duplicate role for a user who already holds it.
          const alreadyAdmin = isOrgAdminForCurrentOrg(existing);
          updated = {
            ...existing,
            name: formUser.name || existing.name,
            phone: formUser.phone || existing.phone,
            roles: alreadyAdmin
              ? existing.roles || []
              : [...(existing.roles || []), newRole],
          };
        } else {
          // New user: create the document with the Org Administrator role.
          updated = {
            id: null,
            name: formUser.name,
            email,
            phone: formUser.phone,
            roles: [newRole],
          };
        }
        updated.orgs = [...new Set((updated.roles || []).map((r) => r.org))];
        return webservice.user.save(updated);
      })
      .then(() => {
        setBusy(true);
        displayAddOrgAdmin(false);
        getUsers();
      })
      .catch((saveError) => {
        displayError();
        return Promise.reject(saveError);
      });
  };

  const resetUser = () => {
    editUser(true, {}, false);
    displayDelete(false);
  };

  const saveUser = (user, isNew) => {
    // Duplicate-email gate FIRST: a create must have a unique email. The email
    // is the pharmacy-user business key, so before any save mutation (or other
    // queries) we check whether it already belongs to a user and fail
    // immediately if it does — never creating a duplicate or silently mutating
    // the existing user.
    return webservice.user.perEmail(user.email).then((data) => {
      const duplicate = data ? data.userByEmail : undefined;
      if (isNew && duplicate) {
        setBusy(false);
        MessagePopupCore.addMessage({
          title: 'Duplicate email',
          message:
            'A user with this email address already exists. Enter a unique email, or edit the existing user instead.',
        });
        return Promise.reject(new Error('DUPLICATE_EMAIL'));
      }

      user.orgs = user.roles
        ? [...new Set(user.roles.map((role) => role.org))]
        : [];
      return webservice.user
        .save(user)
        .then(() => {
          setBusy(true);
          resetUser();
          getUsers();
        })
        .catch(displayError);
    });
  };

  const actualDelete = () =>
    webservice.user
      .delete(user)
      .then(() => {
        setBusy(true);
        resetUser();
        getUsers();
      })
      .catch(displayError);

  const getUsers = () => {
    let orgID =
      appStore.currentOrg ||
      (security.hasAnyRole([roles.dev, roles.admin]) ? null : 'not found');
    if (orgID) {
      webservice.user
        .perOrg(orgID)
        .then((data) => {
          setUsers(data.usersByOrg);
          setBusy(false);
        })
        .catch(displayError);
    } else {
      webservice.user
        .all()
        .then((data) => {
          setUsers(data.users);
          setBusy(false);
        })
        .catch(displayError);
    }
  };

  useEffect(() => {
    if (prevOrg !== appStore.currentOrg) {
      setBusy(true);
      security.hasAnyRole([roles.dev, roles.admin, roles.orgAdmin])
        ? getUsers()
        : Pages.home.forward();
    }
  }, [appStore.currentOrg]);

  useEffect(() => {
    if (
      appStore.currentOrg ||
      security.hasAnyRole([roles.dev, roles.admin])
    ) {
      getUsers();
    } else {
      setBusy(false);
    }
  }, []);

  return (
    <div key="listings" className="home-wrapper">
      {appStore.currentOrg && security.hasAnyRole([roles.dev, roles.admin]) && (
        <div>
          <Button
            onClick={() => {
              appStore.currentOrg = undefined;
              Pages.companies.forward();
            }}
            label="Back to companies"
            className="button--naked flex align-items-center p-none"
            children={Icon.iconArrowLeft('blue mr-spacing--small')}
            childrenPosition={Button.CHILDREN_POSITION.BEFORE}
          />
        </div>
      )}
      <div className="home-content">
        <h1 className="mb-zero">
          {appStore.currentOrgRecord ? appStore.currentOrgRecord.name : 'Users'}
        </h1>
      </div>
      <div className="buttons flex">
        <Button
          label="Add User"
          className="mr-spacing flex align-items-center"
          onClick={() =>
            // In the company-scoped view (a company is selected — an
            // Administrator viewing a company's users, or an Org Administrator)
            // "Add User" adds an EXISTING user as an Org Administrator of that
            // company. In the unscoped Administrator view it creates a new user.
            appStore.currentOrg ? openAddOrgAdmin() : editUser(true, {}, true)
          }
          children={
            <span className="icon--round ml-spacing">{Icon.iconPlus()}</span>
          }
          childrenPostion={Button.CHILDREN_POSITION.AFTER}
        />
      </div>
      <div>
        <Table
          list={sortByLastUpdatedDesc(users)}
          columns={columns(editUser, deleteUser)}
          filters={true}
          showLimit={50}
          ajaxSpinner={isBusy}
        />
      </div>
      {showUser && (
        <User
          onClose={resetUser}
          onSave={saveUser}
          isNew={isNew}
          user={user}
          orgs={orgs}
        />
      )}
      {showAddOrgAdmin && (
        <AddOrgAdmin
          company={appStore.currentOrgRecord}
          onClose={closeAddOrgAdmin}
          onSave={saveOrgAdmin}
        />
      )}
      {showDelete && (
        <PopupWindow
          className="popup-window--large"
          onCloseButtonCallback={resetUser}
          footerChildren={[
            <Button
              label="Cancel"
              onClick={resetUser}
              key="close-button"
              className="button--black"
            />,
            <Button
              label="Delete"
              busy={isBusy}
              onClick={actualDelete}
              key="save-button"
            />,
          ]}
        >
          <h3>Are you sure?</h3>
          <p>You are about to delete a user.</p>
        </PopupWindow>
      )}
    </div>
  );
};

export default observer(Users);
